import * as React from 'react';
import styled, { css, ThemeContext } from 'styled-components';

import { cssDeclaration } from '../../Utils';
import Button, { IButtonIcon, IButtonProps } from '../Button';
import { makeButtonCSS, makeVariantTheme } from '../StyledButton';

export interface IActionButtonProps extends IButtonProps, React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant: string;
  iconProps?: IButtonIcon;
  selectedIconProps?: IButtonIcon;
  selected?: boolean;
}

const StyledActionButton = styled(
  props => <Button variant={props.variant} {...props}>{props.children}</Button>
)`

  ${ ({ theme: { themeTwo }, variant, selected, error }) => {

    const variantTheme = makeVariantTheme(themeTwo, variant).variantTheme;
    const errorTheme = makeVariantTheme(themeTwo, variant).variantErrorTheme;
    const selectedTheme = makeVariantTheme(themeTwo, variant).variantSelectedTheme;
    const hoverTheme = makeVariantTheme(themeTwo, variant).variantHoverTheme;
    const activeTheme = makeVariantTheme(themeTwo, variant).variantActiveTheme;
    const focusTheme = makeVariantTheme(themeTwo, variant).variantFocusTheme;
    const disabledTheme = makeVariantTheme(themeTwo, variant).variantDisabledTheme;

    const selectedColor = selected ? selectedTheme.font.color : variantTheme.font.color;

    return css`
      ${ makeButtonCSS(themeTwo, selected ? selectedTheme : variantTheme) };
      ${ error && makeButtonCSS(themeTwo, errorTheme) };

      svg {
        ${selectedColor && cssDeclaration(themeTwo, 'fill', selectedColor)};
      }

      &:hover {
        svg {
          ${hoverTheme.font.color && cssDeclaration(themeTwo, 'fill', hoverTheme.font.color)};
        }
      }

      &:focus {
        svg {
          ${focusTheme.font.color && cssDeclaration(themeTwo, 'fill', focusTheme.font.color)};
        }
      }

      &:active {
        svg {
          ${activeTheme.font.color && cssDeclaration(themeTwo, 'fill', activeTheme.font.color)};
        }
      }

      &:disabled {
        svg {
          ${disabledTheme.font.color && cssDeclaration(themeTwo, 'fill', disabledTheme.font.color)};
        }
      }
    `;
  } }
`;

export const ActionButton = React.forwardRef<HTMLButtonElement, IActionButtonProps>(
  (
    {
      variant,
      children,
      selected = false,
      iconProps,
      selectedIconProps,
      ...rest
    },
    ref
  ) => {
    const { themeTwo: { button } } = React.useContext(ThemeContext);

    const availableVariants = Object.keys(button.overrides);

    let buttonType = variant;
    if (!availableVariants.includes(variant)) {
      console.error(`
      ${ variant } is not available. The following variants are available for ${ name }'s theme: ${ Object.values(availableVariants) }
    `);
      buttonType = 'primary';
    }

    return (
      <StyledActionButton ref={ref} variant={buttonType} selected={selected} iconProps={selected ? selectedIconProps : iconProps} {...rest}>
        {children}
      </StyledActionButton>
    );

  });

export default ActionButton;
